import React, { useState, useEffect, useRef } from "react";
import VerificationApprovalModal from "../../../common/VerificationApprovalModal/VerificationApprovalModal.jsx";
import greenTick from "../../../resources/icons/greenTick.svg";
import warningInfoIcon from "../../../resources/icons/warning-info.svg";
import revokedIcon from "../../../resources/icons/revoked-lottie-icon.svg";
import { formatDate } from "../../../utils/formatDate.jsx";
import { useTranslation } from "react-i18next";
import EntityConstants from "../../../configs/EntityConstants.jsx";
import Toast from "../../../common/Toast/Toast.jsx";
import { GET, POST } from "../../../service/CRUDService.jsx";
import DynamicVerificationApprovalModal from "../../../common/DynamicVerificationApprovalModal/DynamicVerificationApprovalModal.jsx";
import { useNavigate } from "react-router-dom";

const LLM_POLLING_INTERVAL = 3000;

const VerificationApprovalsLongCard = (props) => {
  const {
    transaction_id,
    verifier_org_name,
    created_at,
    verification_cost,
    vc_status,
    // onCheckboxChange,
    // isChecked,

    verifier_name,
    document_type,
    onClick,
    approval_status,
    refreshTable,
  } = props;

  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionType, setActionType] = useState("");
  const [issuerComments, setIssuerComments] = useState("");
  const [isFetchingApiData, setIsFetchingApiData] = useState(false);
  const [dynamicModalData, setDynamicModalData] = useState(null);
  const [isDynamicModalOpen, setIsDynamicModalOpen] = useState(false);
  const [isLlmPolling, setIsLlmPolling] = useState(false);
  const llmPollingIntervalRef = useRef(null);

  const fetchMatchingVCs = async () => {
    try {
      const response = await GET(
        `${EntityConstants.GET_VERIFICATION_APPROVALS_LIST}${transaction_id}/search`,
      );
      const data = await response.json();

      if (data.error) {
        Toast({
          type: "error",
          title: t("verificationApprovals:error"),
          message: data.error,
        });
        return [];
      } else {
        return data;
      }
    } catch (error) {
      Toast({
        type: "error",
        title: t("verificationApprovals:error"),
        message: t("verificationApprovals:failed_load_matching_vcs"),
      });
      return [];
    }
  };

  const stopLlmPolling = () => {
    if (llmPollingIntervalRef.current) {
      clearInterval(llmPollingIntervalRef.current);
      llmPollingIntervalRef.current = null;
    }
    setIsLlmPolling(false);
  };

  const getLlmStatus = (data) =>
    data?.data?.llm_report_data?.status ||
    data?.data?.status ||
    data?.data?.llm_report_status ||
    data?.status;

  const isLlmInProgress = (status) =>
    status === "In-Progress" || status === "In Progress";

  const isLlmReportEmpty = (data) => {
    const report = data?.data?.llm_report_data;
    return !report || Object.keys(report).length === 0;
  };

  const startLlmPolling = () => {
    if (llmPollingIntervalRef.current) return;

    llmPollingIntervalRef.current = setInterval(async () => {
      try {
        const response = await GET(
          `${EntityConstants.VERIFICATION_REPORT_DETAILS}/${transaction_id}`,
        );
        const data = await response.json();

        if (data?.success) {
          const llmStatus = getLlmStatus(data);
          const shouldContinue =
            isLlmInProgress(llmStatus) || isLlmReportEmpty(data);

          setDynamicModalData({
            llmReportData: data.data.llm_report_data || {},
            defaultFields: data.data.default_fields || [],
          });

          if (!shouldContinue) {
            stopLlmPolling();
          }
        }
      } catch (error) {
        stopLlmPolling();
      }
    }, LLM_POLLING_INTERVAL);
  };

  const fetchVerificationReportData = async () => {
    setIsFetchingApiData(true);
    try {
      const response = await GET(
        `${EntityConstants.VERIFICATION_REPORT_DETAILS}/${transaction_id}`,
      );
      const data = await response.json();

      if (data.success) {
        const llmStatus = getLlmStatus(data);
        const shouldPoll = isLlmInProgress(llmStatus) || isLlmReportEmpty(data);

        setDynamicModalData({
          llmReportData: data.data.llm_report_data || {},
          defaultFields: data.data.default_fields || [],
        });
        setIsDynamicModalOpen(true);

        if (shouldPoll) {
          setIsLlmPolling(true);
          startLlmPolling();
        } else {
          stopLlmPolling();
        }
      } else {
        Toast({
          type: "error",
          title: t("verificationApprovals:error"),
          message:
            data.message || t("verificationApprovals:failed_to_fetch_details"),
        });
      }
    } catch (error) {
      Toast({
        type: "error",
        title: t("verificationApprovals:error"),
        message: t("verificationApprovals:failed_to_fetch_details"),
      });
    } finally {
      setIsFetchingApiData(false);
    }
  };

  const handleReject = async (event) => {
    event.stopPropagation();
    setActionType("Rejected");

    const isLegacyType = ["Legacy", "Data", "OCR"].includes(document_type);

    if (isLegacyType) {
      const vcs = await fetchMatchingVCs();
      if (!vcs || vcs.length === 0) {
        await fetchVerificationReportData();
      } else {
        setIsModalOpen(true);
      }
    } else {
      setIsModalOpen(true);
    }
  };

  const handleAccept = async (event) => {
    event.stopPropagation();
    setActionType("Approved");

    const isLegacyType = ["Legacy", "Data", "OCR"].includes(document_type);

    if (isLegacyType) {
      const vcs = await fetchMatchingVCs();
      if (!vcs || vcs.length === 0) {
        await fetchVerificationReportData();
      } else {
        setIsModalOpen(true);
      }
    } else {
      setIsModalOpen(true);
    }
  };

  const handleModalConfirm = async () => {
    const body = {
      transaction_id: transaction_id,
      verification_approval_status: actionType,
      ...(issuerComments?.trim() && { issuer_comments: issuerComments.trim() }),
    };
    try {
      const response = await POST(
        EntityConstants.GET_VERIFICATION_APPROVALS_LIST,
        body,
      );
      if (response.ok) {
        Toast({
          type: "success",
          title: t("verificationApprovals:transaction_updated"),
          message: `${t("verificationApprovals:transaction_has_been")} ${actionType.toLowerCase()} ${t("verificationApprovals:successfully")}!`,
        });
        if (refreshTable) {
          refreshTable();
        }
        navigate(0);
      } else {
        const errorData = await response.json();
        Toast({
          type: "error",
          title: t("verificationApprovals:error"),
          message: errorData.message || t("verificationApprovals:error_updating_transaction"),
        });
      }
    } catch (error) {
      console.error("Error during the POST request:", error);
      Toast({
        type: "error",
        title: t("verificationApprovals:error"),
        message: t("verificationApprovals:error_updating_transaction_status"),
      });
    }
    setIsModalOpen(false);
    setIssuerComments("");
  };

  const handleCardClick = (event) => {
    event.preventDefault();
    if (onClick) {
      onClick();
    }
  };

  //! BULK ACTIONS - Can be resuable in future
  // const handleCheckboxChange = (event) => {
  //   event.stopPropagation();
  //   onCheckboxChange(transaction_id, event.target.checked);
  // };

  const handleDynamicModalClose = (shouldNavigate = false) => {
    stopLlmPolling();
    setDynamicModalData(null);
    setIsDynamicModalOpen(false);

    if (shouldNavigate) {
      if (refreshTable) {
        refreshTable();
      }

      navigate(0);
    }
  };

  useEffect(() => {
    return () => {
      if (llmPollingIntervalRef.current) {
        clearInterval(llmPollingIntervalRef.current);
      }
    };
  }, []);

  const getActionStatus = () => {
    if (approval_status === "Approved") {
      return (
        <p
          id={`verification-approvals-card-status-${transaction_id}`}
          className="font-semibold text-cfPrimaryButton"
        >
          {t("verificationApprovals:approved")}
        </p>
      );
    }
    if (approval_status === "Rejected") {
      return (
        <p
          id={`verification-approvals-card-status-${transaction_id}`}
          className="font-semibold"
          style={{ color: "#D94D20" }}
        >
          {t("verificationApprovals:rejected")}
        </p>
      );
    }

    return (
      <>
        <button
          id={`verification-approvals-card-approve-${transaction_id}`}
          className="flex rounded-[8px] items-center justify-center gap-2 w-[10rem] h-[2.5rem] lg:w-[10rem] lg:px-0 bg-cfPrimaryButton text-white font-medium lg:font-semibold text-sm lg:text-md hover:bg-cfPrimaryDark transition duration-200 focus:outline-none"
          onClick={handleAccept}
          disabled={isFetchingApiData}
        >
          <div>
            {isFetchingApiData
              ? t("verificationApprovals:loading")
              : t("verificationApprovals:approve")}
          </div>
        </button>
        <button
          id={`verification-approvals-card-reject-${transaction_id}`}
          className="flex rounded-[8px] items-center justify-center gap-2 w-[10rem] h-[2.5rem] lg:w-[10rem] lg:px-0 bg-white text-black font-medium lg:font-semibold text-sm lg:text-md border border-gray-800 hover:!bg-gray-100 transition duration-200 focus:outline-none"
          onClick={handleReject}
          disabled={isFetchingApiData}
        >
          <div>
            {isFetchingApiData
              ? t("verificationApprovals:loading")
              : t("verificationApprovals:reject")}
          </div>
        </button>
      </>
    );
  };

  return (
    <div
      id={`verification-approvals-card-${transaction_id}`}
      className="flex bg-white items-center min-h-[5rem] border-b py-1 px-4 cursor-pointer "
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleCardClick(e);
        }
      }}
      role="button"
      tabIndex={0}
    >
      {/* BULK ACTIONS - Can be resuable in future
      <div
        className="flex w-[2.0%] justify-center items-center bg-white"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <input
          type="checkbox"
          checked={isChecked}
          onChange={handleCheckboxChange}
          className="w-5 h-5 border-2 border-gray-500 rounded-lg accent-blue-600 appearance-auto cursor-pointer"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
      */}
      {/* <div className="flex w-[2.0%] justify-center items-center"></div> */}
      <div className="flex w-[14.29%] justify-center items-center cursor-pointer ">
        <p className="font-regular text-black text-[14px]">
          {verifier_name || t("verificationApprovals:na")}
        </p>
      </div>
      <div className="flex w-[14.29%] justify-center text-center cursor-pointer ">
        <p className="font-regular text-black text-[14px]">
          {verifier_org_name || t("verificationApprovals:na")}
        </p>
      </div>
      <div className="flex w-[14.29%] justify-center items-center cursor-pointer">
        <p className="font-regular text-black text-[14px]">
          {created_at
            ? formatDate(created_at)
            : t("verificationApprovals:no_date_available")}
        </p>
      </div>
      <div className="flex w-[14.29%] justify-center items-center cursor-pointer">
        {vc_status === "Verified" ? (
          <img src={greenTick} alt="Verified" className="w-10 h-10" />
        ) : vc_status === "Revoked" ? (
          <img src={revokedIcon} alt="Not Verified" className="w-11 h-11" />
        ) : (
          <img src={warningInfoIcon} alt="Not Verified" className="w-11 h-11" />
        )}
      </div>

      <div className="flex w-[14.29%] justify-center items-center cursor-pointer">
        <p className="font-regular text-black text-[14px]">
          {verification_cost && parseFloat(verification_cost) !== 0
            ? `₹ ${(parseFloat(verification_cost) / 100).toFixed(2)}`
            : t("verificationApprovals:free_of_charge")}
        </p>
      </div>
      <div className="flex w-[14.29%] justify-center items-center cursor-pointer">
        <p className="font-regular text-black text-[14px]">
          {document_type || t("verificationApprovals:na")}
        </p>
      </div>
      <div className="flex w-[14.29%] justify-center items-center space-x-4">
        {getActionStatus()}
      </div>

      {/* Confirmation Modal */}
      <VerificationApprovalModal
        isModalOpen={isModalOpen}
        toggleModal={setIsModalOpen}
        actionType={actionType}
        handleConfirmAction={handleModalConfirm}
        issuerComments={issuerComments}
        setIssuerComments={setIssuerComments}
        isLegacy={props.isLegacy}
        matchingVCs={props.matchingVCs}
        vcStatus={vc_status}
        transaction_id={transaction_id}
      />

      {isDynamicModalOpen && dynamicModalData && (
        <DynamicVerificationApprovalModal
          isModalOpen={isDynamicModalOpen}
          toggleModal={setIsDynamicModalOpen}
          actionType={actionType}
          transaction_id={transaction_id}
          apiData={dynamicModalData.llmReportData}
          defaultFields={dynamicModalData.defaultFields}
          onClose={handleDynamicModalClose}
          isLlmPolling={isLlmPolling}
        />
      )}
    </div>
  );
};

export default VerificationApprovalsLongCard;
